import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  DEFAULT_SUBSCRIPTION,
  ISubscription,
} from "@/server/types/subscription";

type State = {
  subscription: ISubscription;
  planRestricted: boolean; // session-only: set when BE returns 403 PlanFeatureRestricted
};

type Actions = {
  setSubscription: (subscription: ISubscription) => void;
  resetSubscription: () => void;
  setPlanRestricted: (value: boolean) => void;
};

const initialState: State = {
  subscription: DEFAULT_SUBSCRIPTION,
  planRestricted: false,
};

export const useSubscriptionState = create<State & Actions>()(
  persist(
    (set) => ({
      ...initialState,

      setSubscription: (subscription: ISubscription) => set({ subscription }),
      resetSubscription: () => set(initialState),
      setPlanRestricted: (value: boolean) => set({ planRestricted: value }),
    }),
    {
      name: "subscription-state",
      version: 1,
      // Only persist the subscription object — planRestricted is session-only
      partialize: (state) => ({ subscription: state.subscription }),
      // v0 → v1: cancel_at_period_end was added to ISubscription.
      // Merge stored state onto DEFAULT_SUBSCRIPTION so no field is ever undefined.
      migrate: (persistedState: unknown, version: number) => {
        const stored = persistedState as {
          subscription?: Partial<ISubscription>;
        };
        if (version === 0) {
          return {
            subscription: {
              ...DEFAULT_SUBSCRIPTION,
              ...(stored.subscription ?? {}),
              cancel_at_period_end:
                stored.subscription?.cancel_at_period_end ?? false,
            },
          };
        }
        return persistedState as { subscription: ISubscription };
      },
    },
  ),
);
